import { fetchAuthSession, signIn, signOut, signUp } from 'aws-amplify/auth';
import { AppConst } from 'constants/appConst';
import { useApiState } from 'hooks/useApiState';
import { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import type { AcctStateType } from 'stores/atoms/acctState';
import { AcctState } from 'stores/atoms/acctState';
import type { MasterStateType } from 'stores/atoms/masterState';
import { MasterState } from 'stores/atoms/masterState';
import { ModalConfirmState } from 'stores/atoms/modalConfirmState';
import { NoticeMessageState } from 'stores/atoms/noticeMessageState';

/**
 * アプリケーション共通カスタムフック
 */
export const useAppState = () => {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState, setAcctState] = useAtom(AcctState);
  const [masterState, setMasterState] = useAtom(MasterState);
  const [, setNoticeMessageState] = useAtom(NoticeMessageState);
  const [, setModalConfirmState] = useAtom(ModalConfirmState);
  const { postAcctStateRef, postAcctVerify, postMasterRef } = useApiState();

  const acctStateDefault: AcctStateType = useMemo(
    () => ({
      user: null,
      company: null,
      usage: null,
      activeContent: null,
      initialized: false,
      proxyLogin: false,
      proxyCompanyCd: null,
    }),
    []
  );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _signIn = async (mailAddress: string): Promise<void> => {
    await signIn({
      username: mailAddress,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
      },
    });
  };

  /* ----------------------------------------------------------------------- */
  /* パブリック関数定義 */
  /* ----------------------------------------------------------------------- */
  return {
    /**
     * Cognitoのサインイン状態を取得します。
     */
    isSignedIn: useCallback(async (): Promise<boolean> => {
      return (await fetchAuthSession()).identityId ? true : false;
    }, []),

    /**
     * サインアップリクエストをCognitoに送信し、認証コードを受信します。
     * 認証済の場合はサインアウトしてから再度サインアップします。
     */
    signUpRequest: useCallback(
      async (mailAddress: string) => {
        try {
          // 現在のサインイン状態を破棄
          if ((await fetchAuthSession()).identityId) {
            await signOut();
            setAcctState(acctStateDefault);
          }
          // アカウントの発行
          await signUp({
            username: mailAddress,
            password: 'dummypassword',
            options: {
              userAttributes: {
                email: mailAddress,
              },
            },
          });

          // 認証コードの送信
          await _signIn(mailAddress);
        } catch (e: unknown) {
          if ((e as { name?: string }).name === 'UsernameExistsException') {
            await _signIn(mailAddress);
          } else {
            throw e;
          }
        }
      },
      [acctStateDefault, setAcctState]
    ),

    /**
     * サインインリクエストをCognitoに送信し、認証コードを受信します。
     * 存在しないアカウントの場合はfalseを返却します。
     */
    signInRequest: useCallback(
      async (mailAddress: string): Promise<boolean> => {
        const response = await postAcctVerify({ mailAddress });

        if (!response.valid) {
          return false;
        }
        await _signIn(mailAddress);

        return true;
      },
      [postAcctVerify]
    ),

    /**
     * サインアウトします。
     */
    signOutRequest: useCallback(async () => {
      try {
        if ((await fetchAuthSession()).identityId) {
          await signOut();
        }
        setAcctState(acctStateDefault);
        return acctStateDefault;
      } catch {
        return acctState;
      }
    }, [acctState, acctStateDefault, setAcctState]),

    /**
     * アカウント関連の状態管理を最新化します。
     */
    initializeAcctState: useCallback(
      async (companyCd: string | null = null) => {
        const proxyLogin = acctState.proxyLogin;
        const proxyCompanyCd = acctState.proxyCompanyCd;
        let retAcctState: AcctStateType = {
          ...acctStateDefault,
          initialized: true,
        };
        try {
          const session = await fetchAuthSession();

          // 並列実行する処理を配列で定義
          const promises = [];

          // マスター情報の取得
          if (!masterState.initialized) {
            promises.push(
              postMasterRef().then(masterResponse => {
                setMasterState({ ...masterResponse, initialized: true });
              })
            );
          }
          // 認証済みユーザーの情報取得
          if (session.identityId) {
            promises.push(
              postAcctStateRef({
                target: ['user', 'company', 'usage', 'activeContent'],
                companyCd: companyCd,
                proxyLogin: proxyLogin,
              }).then(response => {
                retAcctState = {
                  ...response,
                  initialized: true,
                  proxyLogin: proxyLogin,
                  proxyCompanyCd: proxyCompanyCd,
                };
              })
            );
          }
          // マスター情報の取得と、認証済みユーザーの情報取得を並列実行
          await Promise.all(promises);
        } catch {
          // 既存挙動維持: 例外時も default acctState を設定して返す（throw しない）
        } finally {
          setAcctState(retAcctState);
        }
        return retAcctState;
      },
      [
        acctState.proxyCompanyCd,
        acctState.proxyLogin,
        acctStateDefault,
        masterState.initialized,
        postAcctStateRef,
        postMasterRef,
        setAcctState,
        setMasterState,
      ]
    ),
    initializeAcctStateByProxy: useCallback(
      async (companyCd: string | null = null) => {
        let retAcctState: AcctStateType = {
          ...acctStateDefault,
          initialized: true,
        };
        try {
          const session = await fetchAuthSession();

          // 並列実行する処理を配列で定義
          const promises = [];

          // マスター情報の取得
          if (!masterState.initialized) {
            promises.push(
              postMasterRef().then(masterResponse => {
                setMasterState({ ...masterResponse, initialized: true });
              })
            );
          }
          // 認証済みユーザーの情報取得
          if (session.identityId) {
            promises.push(
              postAcctStateRef({
                target: ['user', 'company', 'usage', 'activeContent'],
                companyCd: companyCd,
                proxyLogin: true,
              }).then(response => {
                retAcctState = {
                  ...response,
                  initialized: true,
                  proxyLogin: true,
                  proxyCompanyCd: companyCd,
                };
              })
            );
          }
          // マスター情報の取得と、認証済みユーザーの情報取得を並列実行
          await Promise.all(promises);
        } catch {
          // 既存挙動維持: 例外時も default acctState を設定して返す（throw しない）
        } finally {
          setAcctState(retAcctState);
        }
        return retAcctState;
      },
      [
        acctStateDefault,
        masterState.initialized,
        postAcctStateRef,
        postMasterRef,
        setAcctState,
        setMasterState,
      ]
    ),

    /**
     * アカウント関連の状態管理を更新します。
     */
    updateAcctState: useCallback(
      async (
        target: ('user' | 'company' | 'usage' | 'activeContent')[],
        companyCd: string | null = null
      ) => {
        const response = await postAcctStateRef({
          target: target,
          companyCd: companyCd,
          proxyLogin: acctState.proxyLogin,
        });

        const updatedAcctState = { ...acctState };

        if (target.includes('user')) {
          updatedAcctState.user = response.user;
        }
        if (target.includes('company')) {
          updatedAcctState.company = response.company;
        }
        if (target.includes('usage')) {
          updatedAcctState.usage = response.usage;
        }
        if (target.includes('activeContent')) {
          updatedAcctState.activeContent = response.activeContent;
        }

        setAcctState(updatedAcctState);
        return updatedAcctState;
      },
      [acctState, postAcctStateRef, setAcctState]
    ),

    /**
     * 処理完了メッセージを設定します。
     */
    noticeMessageSuccess: useCallback(
      (message: string): void => {
        setNoticeMessageState({ message: message, status: 'success' });
      },
      [setNoticeMessageState]
    ),

    /**
     * 異常終了メッセージを設定します。
     */
    noticeMessageFailure: useCallback(
      (message: string): void => {
        setNoticeMessageState({ message: message, status: 'failure' });
      },
      [setNoticeMessageState]
    ),

    /**
     * 確認ダイアログを表示します。
     */
    confirm: useCallback(
      (props: { title: string; message: string; onSubmit: () => void }): void => {
        setModalConfirmState({
          open: true,
          title: props.title,
          message: props.message,
          onSubmit: props.onSubmit,
        });
      },
      [setModalConfirmState]
    ),

    /**
     * コンテンツが公開可能かどうかを判定します。
     */
    canPublish: useCallback(
      (contentsTypeDiv: number): boolean => {
        // プランが期限切れの場合は公開不可
        if (acctState.company.plan.isExpired) {
          return false;
        }

        // アクティブなプランを取得
        const activePlan = masterState.mPlans.find(e => e.id === acctState.company.plan.planId);
        if (!activePlan) {
          return false;
        }
        // トライアルプランの場合は、コンテンツ毎に1件のみ公開可能
        if (activePlan.id === AppConst.planId.trial) {
          return (
            acctState.activeContent.publishingCount.filter(
              e => e.contentsTypeDiv === contentsTypeDiv
            )[0].count < 1
          );
        }
        // サブスクリプションプランの場合は、プランの公開可能数を超えている場合に公開不可
        return acctState.usage.publishedCount < activePlan.publishContentCountLimit;
      },
      [acctState, masterState.mPlans]
    ),

    /**
     * アクティブなプランを取得します。
     */
    getActivePlan: useCallback((): MasterStateType['mPlans'][0] | undefined => {
      return masterState.mPlans.find(e => e.id === acctState.company.plan.planId);
    }, [acctState, masterState.mPlans]),

    /**
     * 公開数の上限が無制限かどうかを判定します。
     */
    isPublishedCountNoLimit: useCallback((): boolean => {
      return acctState.company.plan.planId != AppConst.planId.trial;
    }, [acctState]),

    /**
     * 獲得リスト数の上限が無制限かどうかを判定します。
     */
    isMailAddressCountNoLimit: useCallback((): boolean => {
      return acctState.company.plan.planId === AppConst.planId.custom;
    }, [acctState]),

    /**
     * AIテキスト生成数の上限が無制限かどうかを判定します。
     */
    isAiTextCountNoLimit: useCallback((): boolean => {
      return acctState.company.plan.planId === AppConst.planId.custom;
    }, [acctState]),

    /**
     * AI画像生成数の上限が無制限かどうかを判定します。
     */
    isAiImageCountNoLimit: useCallback((): boolean => {
      return acctState.company.plan.planId === AppConst.planId.custom;
    }, [acctState]),
  } as const;
};
